import _isNil from "lodash/isNil";
import { UserPermission } from "core/constants";
import { sessionManager } from "core/instances";
import UrlUtils from "core/lib/url-utils";
import { UserDetails } from "core/services/cef/permission.service.types";

export class UserPermissions {
    private _permissions: Array<any> = [];

    viewActivityLogs = false;
    changePackage = false;
    disconnectPackage = false;
    reconnectPackage = false;
    invite = false;
    approve = false;
    clearError = false;
    viewBalance = false;
    makePayment = false;
    viewStatements = false;

    get inviteOrApprove() {
        return this.invite || this.approve;
    }

    hasPermission = (grantValue: number): boolean => {
        //Have had instances where the values have been returned as ints, but most of the time, strings
        return this._permissions?.some((x: { grantValue: string; }) => parseInt(x.grantValue) === grantValue);
    }

    hasAnyPermission = (...args: number[]): boolean => {
        if (!args.length) {
            return this._permissions.length > 0
        }
        for (let i = 0; i < args.length; i++) {
            if (this.hasPermission(args[i])) {
                return true;
            }
        }

        return false;
    }

    constructor(accountDetails: any = null, isPrimaryUser = false) {
        if (!accountDetails) {
            // console.log("No account details");
            return;
        }

        this._permissions = accountDetails.permissions;

        this.approve = this.hasPermission(UserPermission.authorizations);
        this.changePackage = this.hasPermission(UserPermission.changePackage);
        this.clearError = this.hasPermission(UserPermission.clearError);
        this.disconnectPackage = this.hasPermission(UserPermission.disconnectPackage);
        this.invite = this.hasPermission(UserPermission.addUsers);
        this.makePayment = this.hasAnyPermission(UserPermission.payAccount);
        this.reconnectPackage = this.hasAnyPermission(UserPermission.linkPackage); //TODO: Correct?
        this.viewActivityLogs = isPrimaryUser || this.hasAnyPermission(UserPermission.addUsers, UserPermission.viewUserActivityLogging);
        this.viewBalance = this.hasPermission(UserPermission.balance);
        this.viewStatements = this.hasPermission(UserPermission.statements);

        // console.log(this);
    }
}

class User {
    private _countryCode: string;

    static instance?: User | undefined;

    isLoggedIn? = false;
    accessToken?: string | null;
    email?: string = "";
    fullName?: string = "";
    company?: UserDetails | null;
    companies?: any;
    businessName?: string = "";
    accountDetails: any;
    recentPayment: any;
    hasMultipleCompanies? = false;
    can: UserPermissions = new UserPermissions();

    constructor(countryCode: string) {
        this._countryCode = countryCode;
    }

    hasPermission = (grantValue: number) => {
        return this.can.hasPermission(grantValue);
    }

    get isPrimaryUser() {
        return this.company?.parentCiam == null;
    }

    get countryCode(): string {
        if (!_isNil(this._countryCode)) {
            return this._countryCode.toLowerCase();
        }

        let code = UrlUtils.getLanguageCode();

        if (code?.includes("-")) {
            code = code.split("-")[1];
        }

        return code?.toLowerCase();
    }

    get isSouthAfricanCustomer() {
        return this.countryCode?.toLowerCase() === "za";
    }
}

export const getUser = () => {
    const access = sessionManager.accessToken;

    if (!access) {
        console.log("getUser() - no access");
        User.instance = undefined;
        return undefined;
    }

    const token = access;

    const profile = sessionManager.getProfile();

    const u = new User(profile.countryCode ?? "");

    u.isLoggedIn = token != null;
    u.accessToken = token;

    const user = sessionManager.loggedInUser;

    if (!user) {
        return u;
    }

    let company = user?.companies?.find((x: { customerNumber: any; }) => x.customerNumber === user.customerNumber);

    company = company ?? user?.companies[0];

    u.email = profile.email ?? company.email;

    const agreement = company.agreements.find((x: { id: any; }) => x.id === user.agreementId);

    const accountDetails = agreement ?? company.agreements[0];

    const profileObject = company?.firstName ? company : profile;
    const fullName = `${profileObject?.firstName ?? ""} ${profileObject?.lastName ?? ""}`.trim();

    const businessName = user.businessName ?? "";

    const isPrimaryUser = company.parentCiam == null;

    u.fullName = fullName;
    u.company = company;
    u.businessName = businessName.toTitleCase();
    u.accountDetails = accountDetails;
    u.can = new UserPermissions(accountDetails, isPrimaryUser);
    u.hasMultipleCompanies = user.companies?.length > 1 ?? false;

    User.instance = u;
    return u;
}

export default User;
